import {
  MONTH_LONG,
  MONTH_SHORT,
  WEEKDAY_LONG,
  addDays,
  diffDays,
  formatDayMonth,
  startOfWeek,
  weekdayOf,
} from './dates';

/**
 * Repetição de um evento — o melhor do Calendário do iOS e da Agenda do Google: as opções
 * rápidas saem da data escolhida ("Semanal: cada domingo", "Mensal: no terceiro domingo",
 * "Anual: em 20 de outubro"), há quinzenal e "Personalizar…" (a cada N dias, semanas,
 * meses ou anos; vários dias da semana; o mensal no dia do mês ou no n-ésimo dia da
 * semana) e o fim é escolhido à parte: nunca, numa data ou depois de N vezes.
 *
 * A regra fica guardada em cada data da série (`ScheduleEvent.rule`): é dela que saem as
 * datas criadas, o rótulo ("Semanal · Domingo") e as ocorrências passadas.
 */

export type RecurrenceFreq = 'daily' | 'weekly' | 'monthly' | 'yearly';

/** No dia do mês (`day`) ou no n-ésimo dia da semana (`nth` 1 a 5, -1 = o último). */
export type MonthlyBy = { kind: 'day'; day: number } | { kind: 'weekday'; nth: number; weekday: number };

export type RecurrenceEnd =
  { kind: 'never' } | { kind: 'until'; date: string } | { kind: 'count'; count: number };

export interface RecurrenceRule {
  freq: RecurrenceFreq;
  /** A cada quantos dias, semanas, meses ou anos (1 = todos). */
  interval: number;
  /** A primeira data da regra (o "a partir de"): o intervalo conta a partir dela. */
  start: string;
  /** Semanal: os dias da semana (0 = domingo), em ordem. Nas outras, vazio. */
  weekdays: number[];
  /** Mensal: no dia do mês ou no n-ésimo dia da semana. Nas outras, `null`. */
  monthly: MonthlyBy | null;
  /** Anual: o mês (1 a 12) e o dia. Nas outras, `null`. */
  yearly: { month: number; day: number } | null;
  end: RecurrenceEnd;
  /**
   * As datas excluídas sozinhas ("Só esta data"): a série continua e elas não voltam ao
   * estender o término nem nas ocorrências passadas (ver `skippedDates`). Sem nenhuma, a
   * regra pode vir sem o campo.
   */
  skipped?: SkippedDate[];
}

/** Uma data que saiu da série sozinha — o culto do feriado, por exemplo. */
export interface SkippedDate {
  date: string;
  /** O horário que ela tinha (o aviso cita, e a data restaurada volta nele). */
  time: string;
  /** Por quê, se a pessoa disse ("Feriado"); `''` sem motivo. */
  reason: string;
  /** Quem estava na escala publicada que ia acontecer: recebe "… foi cancelado". */
  notified: string[];
  /** Quando foi excluída (ms). */
  at: number;
}

export const skippedOf = (rule: RecurrenceRule | null | undefined): SkippedDate[] => rule?.skipped ?? [];

export const isSkipped = (rule: RecurrenceRule | null | undefined, date: string): boolean =>
  skippedOf(rule).some((s) => s.date === date);

/** No máximo tantas datas por série de uma vez (o "Nunca" e a data de fim também param aqui). */
export const MAX_SERIES_OCCURRENCES = 104;
export const MAX_INTERVAL = 99;

const ORDINAL = ['', 'primeir', 'segund', 'terceir', 'quart', 'quint'] as const;
const WEEKDAY_LOWER = WEEKDAY_LONG.map((d) => d.toLowerCase());
const WEEKDAY_ABBR = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'] as const;
/** "Segunda" → "segunda-feira" (só no texto corrido; sábado e domingo ficam). */
const weekdayFull = (d: number) => (d === 0 || d === 6 ? WEEKDAY_LOWER[d]! : `${WEEKDAY_LOWER[d]}-feira`);
/** Sábado e domingo são masculinos; de segunda a sexta (as "-feiras"), femininos. */
const masculine = (d: number) => d === 0 || d === 6;

/**
 * "no terceiro domingo", "na terceira terça-feira", "na última sexta-feira" — ou, curto,
 * "3º domingo", "3ª terça", "último domingo", "última sexta".
 */
function nthWeekdayText(nth: number, weekday: number, short = false): string {
  const o = masculine(weekday) ? 'o' : 'a';
  const day = short ? WEEKDAY_LOWER[weekday]! : weekdayFull(weekday);

  if (nth === -1) return `${short ? '' : `n${o} `}últim${o} ${day}`;
  if (short) return `${nth}${o === 'o' ? 'º' : 'ª'} ${day}`;

  return `n${o} ${ORDINAL[nth]}${o} ${day}`;
}

const ymd = (iso: string) => iso.split('-').map(Number) as [number, number, number];
const pad = (n: number) => String(n).padStart(2, '0');
const isoOf = (y: number, m: number, d: number) => `${y}-${pad(m)}-${pad(d)}`;
const daysInMonth = (y: number, m: number) => new Date(Date.UTC(y, m, 0)).getUTCDate();

/** Em que semana do mês a data cai (1 a 5) e se é a última daquele dia da semana. */
export function weekOfMonth(iso: string): { nth: number; last: boolean } {
  const [y, m, d] = ymd(iso);

  return { nth: Math.ceil(d / 7), last: d + 7 > daysInMonth(y, m) };
}

/** O n-ésimo dia da semana do mês (`nth` -1 = o último), ou `null` se o mês não tem. */
function nthWeekday(y: number, m: number, nth: number, weekday: number): string | null {
  const first = isoOf(y, m, 1);
  const offset = (weekday - weekdayOf(first) + 7) % 7;
  const last = daysInMonth(y, m);

  if (nth === -1) {
    const lastDay = isoOf(y, m, last);

    return addDays(lastDay, -((weekdayOf(lastDay) - weekday + 7) % 7));
  }

  const day = 1 + offset + (nth - 1) * 7;

  return day <= last ? isoOf(y, m, day) : null;
}

/** Meses entre os dois meses (ano e mês), de `from` a `to`. */
const monthsBetween = (from: string, to: string) => {
  const [fy, fm] = ymd(from);
  const [ty, tm] = ymd(to);

  return (ty - fy) * 12 + (tm - fm);
};

/** O período (dia, semana, mês ou ano, de `interval` em `interval`) em que a data cai. */
function periodOf(rule: RecurrenceRule, iso: string): number {
  const span = (() => {
    switch (rule.freq) {
      case 'daily':
        return diffDays(rule.start, iso);
      case 'weekly':
        return diffDays(startOfWeek(rule.start), startOfWeek(iso)) / 7;
      case 'monthly':
        return monthsBetween(rule.start, iso);
      case 'yearly':
        return ymd(iso)[0] - ymd(rule.start)[0];
    }
  })();

  return Math.floor(span / rule.interval);
}

/** As datas da regra no período `k` (o da `start` é o 0), em ordem. */
function datesOfPeriod(rule: RecurrenceRule, k: number): string[] {
  const step = k * rule.interval;

  switch (rule.freq) {
    case 'daily':
      return [addDays(rule.start, step)];
    case 'weekly': {
      const week = addDays(startOfWeek(rule.start), step * 7);

      return [...rule.weekdays].sort((a, b) => a - b).map((d) => addDays(week, d));
    }
    case 'monthly': {
      const [sy, sm] = ymd(rule.start);
      const index = sy * 12 + (sm - 1) + step;
      const y = Math.floor(index / 12);
      const m = (index % 12) + 1;
      const by = rule.monthly;

      if (!by) return [];
      if (by.kind === 'day') return by.day <= daysInMonth(y, m) ? [isoOf(y, m, by.day)] : [];

      const date = nthWeekday(y, m, by.nth, by.weekday);

      return date ? [date] : [];
    }
    case 'yearly': {
      const y = ymd(rule.start)[0] + step;
      const at = rule.yearly;

      return at && at.day <= daysInMonth(y, at.month) ? [isoOf(y, at.month, at.day)] : [];
    }
  }
}

/** As datas do padrão entre `from` e `to` (inclusive), sem olhar o fim da regra. */
export function patternDates(rule: RecurrenceRule, from: string, to: string): string[] {
  if (from > to) return [];

  const dates: string[] = [];

  for (let k = periodOf(rule, from) - 1; k <= periodOf(rule, to) + 1; k++) {
    for (const date of datesOfPeriod(rule, k)) if (date >= from && date <= to) dates.push(date);
  }

  return [...new Set(dates)].sort();
}

/**
 * Até onde o "Nunca termina" cria datas: o fim do ano da primeira (todas as ocorrências do
 * ano corrente). A série pode ganhar mais datas depois.
 */
export function neverEndingUntil(start: string): string {
  return `${ymd(start)[0]}-12-31`;
}

/** O último dia que pode ter data para a regra terminar em `count` vezes (com folga). */
function countHorizon(rule: RecurrenceRule, count: number): string {
  const periods = (count + 2) * rule.interval;
  const perPeriod: Record<RecurrenceFreq, number> = { daily: 1, weekly: 7, monthly: 31, yearly: 366 };

  return addDays(rule.start, periods * perPeriod[rule.freq]);
}

/**
 * As datas que a série cria, a partir da `start` e até o fim escolhido — no máximo uma a
 * mais que `MAX_SERIES_OCCURRENCES` (assim `recurrenceError` sabe que passou do limite).
 */
export function seriesDates(rule: RecurrenceRule): string[] {
  const { end } = rule;
  let to = neverEndingUntil(rule.start);

  if (end.kind === 'until') to = end.date;
  if (end.kind === 'count') to = countHorizon(rule, Math.min(end.count, MAX_SERIES_OCCURRENCES + 1));

  const dates = patternDates(rule, rule.start, to).slice(0, MAX_SERIES_OCCURRENCES + 1);

  return end.kind === 'count' ? dates.slice(0, end.count) : dates;
}

/** Por que a regra não vale, ou `null`. */
export function recurrenceError(rule: RecurrenceRule): string | null {
  const { interval, end } = rule;

  if (!Number.isInteger(interval) || interval < 1 || interval > MAX_INTERVAL)
    return `Repita a cada 1 a ${MAX_INTERVAL}.`;
  if (rule.freq === 'weekly' && !rule.weekdays.length) return 'Escolha ao menos um dia da semana.';
  if (end.kind === 'until' && end.date < rule.start) return 'A data de fim precisa ser depois do início.';
  if (end.kind === 'count' && (!Number.isInteger(end.count) || end.count < 1))
    return 'Informe quantas vezes o evento acontece.';
  if (end.kind === 'count' && end.count > MAX_SERIES_OCCURRENCES)
    return `No máximo ${MAX_SERIES_OCCURRENCES} datas de uma vez.`;

  const dates = seriesDates(rule);

  if (!dates.length) return 'A repetição não tem nenhuma data até o fim escolhido.';
  if (dates.length > MAX_SERIES_OCCURRENCES)
    return `Passa de ${MAX_SERIES_OCCURRENCES} datas: escolha uma data de fim mais próxima.`;

  return null;
}

// ===== Rótulos =====

function weekdaysText(days: readonly number[]): string {
  const sorted = [...days].sort((a, b) => a - b);

  if (sorted.join() === '1,2,3,4,5') return 'segunda a sexta';
  if (sorted.join() === '0,6') return 'sábado e domingo';
  if (sorted.length === 1) return WEEKDAY_LONG[sorted[0]!]!;

  const names = sorted.map((d) => WEEKDAY_ABBR[d]!);

  return `${names.slice(0, -1).join(', ')} e ${names.at(-1)}`;
}

function monthlyText(by: MonthlyBy): string {
  return by.kind === 'day' ? `dia ${by.day}` : nthWeekdayText(by.nth, by.weekday, true);
}

function weeklyHead(interval: number): string {
  if (interval === 1) return 'Semanal';
  if (interval === 2) return 'Quinzenal';

  return `A cada ${interval} semanas`;
}

/**
 * O rótulo curto, guardado em cada data da série e mostrado nos cards: "Semanal ·
 * Domingo", "Quinzenal · Domingo", "Semanal · Seg, Qua e Sex", "Mensal · 3º domingo",
 * "Mensal · dia 20", "Anual · 20 de outubro", "Diário", "A cada 3 semanas · Domingo".
 */
export function recurrenceLabel(rule: RecurrenceRule): string {
  const n = rule.interval;

  switch (rule.freq) {
    case 'daily':
      return n === 1 ? 'Diário' : `A cada ${n} dias`;
    case 'weekly': {
      const days = weekdaysText(rule.weekdays);
      const head = weeklyHead(n);

      return `${head} · ${days.charAt(0).toUpperCase()}${days.slice(1)}`;
    }
    case 'monthly':
      return `${n === 1 ? 'Mensal' : `A cada ${n} meses`} · ${rule.monthly ? monthlyText(rule.monthly) : ''}`;
    case 'yearly': {
      const at = rule.yearly
        ? `${rule.yearly.day} de ${MONTH_LONG[rule.yearly.month - 1]!.toLowerCase()}`
        : '';

      return `${n === 1 ? 'Anual' : `A cada ${n} anos`} · ${at}`;
    }
  }
}

/** "termina em 20 dez 2026", "8 vezes" ou "sem data de fim". */
export function recurrenceEndLabel(end: RecurrenceEnd): string {
  if (end.kind === 'until') {
    const [y, m, d] = ymd(end.date);

    return `até ${d} ${MONTH_SHORT[m - 1]} ${y}`;
  }
  if (end.kind === 'count') return `${end.count} ${end.count === 1 ? 'vez' : 'vezes'}`;

  return 'sem data de fim';
}

// ===== Opções rápidas =====

export type RecurrencePresetKey =
  'weekly' | 'biweekly' | 'monthlyNth' | 'monthlyLast' | 'monthlyDay' | 'yearly' | 'daily' | 'weekdays';

export interface RecurrencePreset {
  key: RecurrencePresetKey;
  /** "Semanal: cada domingo", "Mensal: no terceiro domingo"… */
  label: string;
  rule: RecurrenceRule;
}

const NEVER: RecurrenceEnd = { kind: 'never' };

function base(start: string, end: RecurrenceEnd): Omit<RecurrenceRule, 'freq'> {
  return { interval: 1, start, weekdays: [], monthly: null, yearly: null, end };
}

/**
 * As opções rápidas para a data escolhida, na ordem do formulário (as mais comuns numa
 * igreja primeiro). "Mensal: no último…" só aparece quando a data é a última daquele dia
 * da semana no mês. O fim vem de `end` (o que a pessoa já escolheu).
 */
export function recurrencePresets(date: string, end: RecurrenceEnd = NEVER): RecurrencePreset[] {
  const weekday = weekdayOf(date);
  const [, m, d] = ymd(date);
  const { nth, last } = weekOfMonth(date);
  const day = weekdayFull(weekday);
  const b = base(date, end);
  const presets: RecurrencePreset[] = [
    { key: 'weekly', label: `Semanal: cada ${day}`, rule: { ...b, freq: 'weekly', weekdays: [weekday] } },
    {
      key: 'biweekly',
      label: `Quinzenal: ${WEEKDAY_LOWER[weekday]} sim, ${WEEKDAY_LOWER[weekday]} não`,
      rule: { ...b, freq: 'weekly', interval: 2, weekdays: [weekday] },
    },
  ];

  if (nth <= 4)
    presets.push({
      key: 'monthlyNth',
      label: `Mensal: ${nthWeekdayText(nth, weekday)}`,
      rule: { ...b, freq: 'monthly', monthly: { kind: 'weekday', nth, weekday } },
    });
  if (last)
    presets.push({
      key: 'monthlyLast',
      label: `Mensal: ${nthWeekdayText(-1, weekday)}`,
      rule: { ...b, freq: 'monthly', monthly: { kind: 'weekday', nth: -1, weekday } },
    });

  presets.push(
    {
      key: 'monthlyDay',
      label: `Mensal: no dia ${d}`,
      rule: { ...b, freq: 'monthly', monthly: { kind: 'day', day: d } },
    },
    {
      key: 'yearly',
      label: `Anual: em ${d} de ${MONTH_LONG[m - 1]!.toLowerCase()}`,
      rule: { ...b, freq: 'yearly', yearly: { month: m, day: d } },
    },
    { key: 'daily', label: 'Todos os dias', rule: { ...b, freq: 'daily' } },
    {
      key: 'weekdays',
      label: 'Dias de semana (segunda a sexta)',
      rule: { ...b, freq: 'weekly', weekdays: [1, 2, 3, 4, 5] },
    }
  );

  return presets;
}

const sameRule = (a: RecurrenceRule, b: RecurrenceRule) =>
  a.freq === b.freq &&
  a.interval === b.interval &&
  [...a.weekdays].sort().join() === [...b.weekdays].sort().join() &&
  JSON.stringify(a.monthly) === JSON.stringify(b.monthly) &&
  JSON.stringify(a.yearly) === JSON.stringify(b.yearly);

/** A opção rápida que é esta regra na data (o fim não conta), ou `null` (personalizada). */
export function presetOf(rule: RecurrenceRule, date: string): RecurrencePresetKey | null {
  return recurrencePresets(date).find((p) => sameRule(p.rule, rule))?.key ?? null;
}

/**
 * A regra quando a data muda: a opção rápida acompanha a data (semanal de domingo vira
 * semanal de terça); a personalizada fica, só com o início novo — e o semanal sem nenhum
 * dia ganha o da data.
 */
export function ruleForDate(rule: RecurrenceRule, from: string, to: string): RecurrenceRule {
  const key = presetOf(rule, from);
  const preset = key ? recurrencePresets(to, rule.end).find((p) => p.key === key) : undefined;

  if (preset) return preset.rule;
  // "Mensal: no último…" não existe na data nova: fica o n-ésimo dela.
  if (key === 'monthlyLast') return recurrencePresets(to, rule.end).find((p) => p.key === 'monthlyNth')!.rule;

  return {
    ...rule,
    start: to,
    weekdays: rule.freq === 'weekly' && !rule.weekdays.length ? [weekdayOf(to)] : rule.weekdays,
  };
}

/** "Cria 12 datas, de 25 out a 10 jan." — o resumo antes de salvar. */
export function seriesSummary(rule: RecurrenceRule): string {
  const dates = seriesDates(rule);
  const first = dates[0];
  const last = dates.at(-1);

  if (!first || !last) return '';
  if (dates.length === 1) return `Cria 1 data: ${formatDayMonth(first)}.`;

  return `Cria ${dates.length} datas, de ${formatDayMonth(first)} a ${formatDayMonth(last)}.`;
}

/**
 * A regra de uma série antiga, de quando só existia o semanal: o rótulo "Semanal · …" e o
 * dia da semana da primeira data.
 */
export function legacyWeeklyRule(firstDate: string): RecurrenceRule {
  return { ...base(firstDate, NEVER), freq: 'weekly', weekdays: [weekdayOf(firstDate)] };
}
